import type { ComponentType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface DrawerSectionProps {
  icon?: ComponentType<{ className?: string }>;
  title?: ReactNode;
  description?: ReactNode;
  /** Number shown in a pill after the title. */
  count?: number;
  /** Shown on the right of the header, e.g. a status badge or a button. */
  aside?: ReactNode;
  /** Set false for content that brings its own padding (lists, tabs). */
  padded?: boolean;
  className?: string;
  children?: ReactNode;
}

/** White card used for every section inside a drawer. */
export function DrawerSection({
  icon: Icon,
  title,
  description,
  count,
  aside,
  padded = true,
  className,
  children,
}: DrawerSectionProps) {
  const hasHeader = Boolean(title || description || aside);

  return (
    <section className={cn('overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs', className)}>
      {hasHeader && (
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            {title && (
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                {Icon && <Icon className="h-4 w-4 text-[#4369a0]" />}
                {title}
                {count !== undefined && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {count}
                  </span>
                )}
              </h3>
            )}
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
          {aside}
        </div>
      )}
      {children && <div className={cn(padded && 'space-y-4 p-5')}>{children}</div>}
    </section>
  );
}
